import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, KeyRound, ShieldCheck } from 'lucide-react';
import { getAISettings, saveAISettings, type AISettings } from './api';
import { useCanNTalk } from './context';

const defaults = { qwen: 'qwen-plus', openai: 'gpt-4.1-mini' };
const providerName = (provider: AISettings['provider']) => provider === 'qwen' ? 'Alibaba Qwen' : 'OpenAI';

export default function AIConnectionSettings() {
  const { data, refresh } = useCanNTalk();
  const [settings, setSettings] = useState<AISettings | null>(null);
  const [provider, setProvider] = useState<AISettings['provider']>('qwen');
  const [model, setModel] = useState(defaults.qwen);
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let active = true;
    if (data.role !== 'admin') { setLoading(false); return; }
    getAISettings().then(result => {
      if (!active) return;
      setSettings(result); setProvider(result.provider); setModel(result.model);
    }).catch(caught => { if (active) setError(caught instanceof Error ? caught.message : '未能載入 AI 設定。'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [data.role]);

  if (data.role !== 'admin') return <div className="page cnt-page cnt-studio-denied"><ShieldCheck size={38} /><h1>AI 設定只供管理員使用。</h1><p>請用管理員存取碼登入，再設定對話服務。</p><Link className="primary-button" to="/scenarios">返回情境練習 <ArrowRight size={17} /></Link></div>;

  const existingKey = settings?.provider === provider && settings.keyConfigured;
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!settings?.editable || busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await saveAISettings({ provider, model: model.trim(), ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}) });
      setApiKey(''); setSettings(result); setProvider(result.provider); setModel(result.model);
      await refresh();
      setNotice('AI 設定已儲存。開一場新練習，選擇「真實 AI 對話」開始測試。');
    } catch (caught) { setError(caught instanceof Error ? caught.message : '未能儲存 AI 設定。'); }
    finally { setBusy(false); }
  }

  return <div className="page cnt-page cnt-ai-settings">
    <div className="cnt-title"><div><h1>連接 AI 對話</h1><p>Bring your partners into the conversation.</p></div></div>
    <div className="cnt-ai-settings-grid">
      <section className="cnt-panel cnt-ai-form-panel">
        <div className="cnt-section-heading"><h2>對話服務 · AI provider</h2><KeyRound size={20} /></div>
        {loading ? <p role="status">正在載入 AI 設定…</p> : !settings ? <p className="cnt-error" role="alert">{error || '未能載入 AI 設定，請重新整理頁面。'}</p> : <form onSubmit={event => void save(event)}>
          <fieldset disabled={busy || !settings.editable} className="cnt-ai-fields">
            <label>AI 服務 Provider<select value={provider} onChange={event => {
              const next = event.target.value as AISettings['provider'];
              setProvider(next); setModel(settings.provider === next ? settings.model : defaults[next]); setApiKey(''); setNotice('');
            }}><option value="qwen">Alibaba Qwen</option><option value="openai">OpenAI</option></select></label>
            {provider === 'qwen' && <p className="cnt-ai-connection-note">使用 Alibaba Model Studio 新加坡／國際區域 API key。</p>}
            <label>對話模型 Model<input value={model} onChange={event => { setModel(event.target.value); setNotice(''); }} maxLength={100} required spellCheck={false} /></label>
            <label>API key<input aria-label="API key" type="password" value={apiKey} onChange={event => { setApiKey(event.target.value); setNotice(''); }} autoComplete="off" spellCheck={false} required={!existingKey} placeholder={existingKey ? '已儲存；留空可保留現有 key' : '輸入你嘅 API key'} /><small>{existingKey ? `${providerName(provider)} API key 已設定。唔會喺頁面顯示原有 key。` : `輸入 ${providerName(provider)} API key，先可以開始真實對話。`}</small></label>
            <button className="primary-button" type="submit" disabled={!model.trim() || (!apiKey.trim() && !existingKey)}>{busy ? '儲存中 Saving…' : '儲存 AI 設定 Save settings'} <ArrowRight size={17} /></button>
          </fieldset>
          {!settings.editable && <p className="cnt-ai-connection-note">呢個環境嘅 AI 設定由伺服器管理。請管理員喺伺服器設定服務同 API key。</p>}
          {error && <p className="cnt-error" role="alert">{error}</p>}
          {notice && <div className="cnt-studio-saved" role="status"><Check size={17} />{notice}</div>}
          {settings.keyConfigured && <Link className="secondary-button" to="/scenarios">揀一個情境試傾 <ArrowRight size={17} /></Link>}
        </form>}
      </section>
      <aside className="cnt-panel cnt-blue cnt-ai-explainer"><ShieldCheck size={24} /><h2>用你嘅情境，傾一場真對話。</h2><p>伙伴會按情境、各自個性，同你之前講過嘅內容回應。每個回合會用所選服務嘅 API 額度。</p><p>API key 儲存喺本機伺服器設定，唔會放入瀏覽器儲存空間或對話紀錄。真實 AI 模式會將情境同對話文字傳送畀所選服務。</p><p>角色照片為 AI 合成人物。語音輸入同播放由瀏覽器提供，回饋仍然按文字整理，唔會提供發音或聲調評分。</p><small>現有示範練習會保留原有模式。設定完成後，請開始一場新練習。</small></aside>
    </div>
  </div>;
}
